/**
 * Higiene da base de e-mail.
 *
 * Responde uma pergunta que nenhuma tela respondia: "quais endereços da minha
 * base estão mortos, por quê, e como tiro todos de uma vez".
 *
 * A decisão de desenho que sustenta a tela é o **modo de seleção**. Marcar
 * linha a linha resolve o caso de 5 endereços; o caso real é 450 espalhados por
 * 9 páginas. Por isso existe "selecionar os N do filtro" — as ações passam a
 * operar sobre o filtro no servidor, não sobre ids da página visível, e o
 * número no diálogo de confirmação vem do mesmo WHERE que vai executar.
 */

import { useState, useMemo } from "react";
import { trpc } from "@/lib/trpc";
import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Empty, EmptyHeader, EmptyMedia, EmptyTitle, EmptyDescription,
} from "@/components/ui/empty";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { downloadCsv } from "@/lib/download";
import { toUserFriendlyError } from "@/lib/error-messages";
import { toast } from "sonner";
import {
  ShieldOff, Trash2, RotateCcw, Download, Search, Info, HeartPulse,
} from "lucide-react";
import {
  EMAIL_HYGIENE_REASON_DEFS, type EmailHygieneReason,
} from "@shared/email-hygiene";

const PAGE_SIZE = 50;

export default function EmailHygiene() {
  const { can } = usePermissions();
  const utils = trpc.useUtils();

  const canEdit = can("contacts:edit");
  const canDelete = can("contacts:bulk_delete");
  const canExport = can("email:export_reports");

  const [reason, setReason] = useState<EmailHygieneReason | "all">("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  /**
   * `false` mostra o que foi reativado à mão — o registro do que o usuário
   * desfez. Sem essa aba, reativar um endereço o fazia desaparecer da tela sem
   * deixar rastro, e não havia como conferir nem reverter.
   */
  const [showActive, setShowActive] = useState(true);

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  /** true = a ação vale para TODO o filtro, não só para as linhas marcadas. */
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [confirmAction, setConfirmAction] = useState<"delete" | "reactivate" | null>(null);

  const filter = useMemo(() => ({
    reason: reason === "all" ? undefined : reason,
    search: search.trim() || undefined,
    active: showActive,
  }), [reason, search, showActive]);

  const { data: summary } = trpc.emailHygiene.summary.useQuery();
  const { data, isLoading } = trpc.emailHygiene.list.useQuery({
    filter, limit: PAGE_SIZE, offset: page * PAGE_SIZE,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;

  // Quantos a ação vai atingir de verdade. Só consultado quando o usuário optou
  // pelo modo filtro — no modo seleção o número já está na mão.
  const { data: filterCount } = trpc.emailHygiene.countForAction.useQuery(
    { filter },
    { enabled: selectAllMatching },
  );

  const affected = selectAllMatching ? (filterCount ?? total) : selectedIds.length;

  /** Payload das mutations: ids explícitos OU o filtro inteiro. Nunca os dois. */
  const actionTarget = selectAllMatching ? { filter } : { contactIds: selectedIds };

  const clearSelection = () => {
    setSelectedIds([]);
    setSelectAllMatching(false);
  };

  const afterAction = () => {
    clearSelection();
    setConfirmAction(null);
    utils.emailHygiene.list.invalidate();
    utils.emailHygiene.summary.invalidate();
    utils.emailHygiene.countForAction.invalidate();
    // A base encolheu — a contagem de contatos e os públicos de envio mudam.
    utils.contacts.list.invalidate();
  };

  const deleteMut = trpc.emailHygiene.deleteContacts.useMutation({
    onSuccess: (res) => {
      if (res.skipped.length > 0) {
        // Nunca engolir o pulo: o usuário pediu N e recebeu N-12. Sem esta
        // mensagem a tela mentiria por omissão.
        toast.warning(
          `${res.deleted} contato(s) excluído(s). ${res.skipped.length} mantido(s) por terem oportunidade no CRM.`,
          { duration: 8000 },
        );
      } else {
        toast.success(`${res.deleted} contato(s) excluído(s) da base.`);
      }
      afterAction();
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const reactivateMut = trpc.emailHygiene.reactivate.useMutation({
    onSuccess: (res) => {
      if (res.refused > 0) {
        // Descadastro e denúncia de spam não são reativáveis. Silenciar isso
        // faria o usuário acreditar que religou um envio que segue bloqueado.
        toast.warning(
          `${res.affected} endereço(s) voltaram a receber. ${res.refused} mantido(s) bloqueado(s): ` +
          `quem se descadastrou ou denunciou spam só volta com consentimento novo.`,
          { duration: 9000 },
        );
      } else {
        toast.success(`${res.affected} endereço(s) voltaram a receber e-mail.`);
      }
      afterAction();
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const exportMut = trpc.emailHygiene.exportCsv.useMutation({
    onSuccess: (res) => downloadCsv(res.csv, res.filename),
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const allOnPageSelected = rows.length > 0 && rows.every((r: any) => selectedIds.includes(r.contactId));

  const toggleAllOnPage = () => {
    if (allOnPageSelected) {
      const onPage = new Set(rows.map((r: any) => r.contactId));
      setSelectedIds((prev) => prev.filter((id) => !onPage.has(id)));
      setSelectAllMatching(false);
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...rows.map((r: any) => r.contactId)])]);
    }
  };

  const changeFilter = (fn: () => void) => {
    // Trocar de filtro com seleção pendente é a receita para apagar o conjunto
    // errado: "todos do filtro" passaria a significar outra coisa.
    fn();
    setPage(0);
    clearSelection();
  };

  // Só os motivos com pelo menos um endereço, mais o que estiver selecionado —
  // um seletor com 10 opções zeradas não ajuda a decidir nada.
  const reasonOptions = (summary ?? []).filter((s: any) => s.count > 0 || s.key === reason);
  const suppressingTotal = (summary ?? [])
    .filter((s: any) => s.suppresses)
    .reduce((acc: number, s: any) => acc + s.count, 0);

  return (
    <PermissionGuard permission="email:view_campaigns">
      <DashboardLayout>
        <div className="space-y-6 p-4 sm:p-6 max-w-full overflow-x-hidden">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-2xl font-semibold flex items-center gap-2">
                <HeartPulse className="h-6 w-6 text-rose-500" />
                Higiene da base
              </h1>
              <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                Endereços que não recebem mais e-mail e o motivo de cada um. Eles já saem
                automaticamente dos envios — excluir aqui é para tirar da sua base de contatos.
              </p>
            </div>
            {canExport && (
              <Button
                variant="outline"
                onClick={() => exportMut.mutate({ filter })}
                disabled={exportMut.isPending || total === 0}
              >
                <Download className="h-4 w-4 mr-2" />
                {exportMut.isPending ? "Gerando…" : "Exportar CSV"}
              </Button>
            )}
          </div>

          {/* Cards por motivo — clicar filtra a tabela. */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            <Card
              role="button"
              tabIndex={0}
              onClick={() => changeFilter(() => setReason("all"))}
              className={`cursor-pointer transition-all hover:shadow-md ${reason === "all" ? "ring-2 ring-foreground/40" : ""}`}
            >
              <CardContent className="pt-4 pb-4">
                <div className="text-sm text-muted-foreground">Total suprimido</div>
                <p className="text-2xl font-bold mt-1">{suppressingTotal.toLocaleString("pt-BR")}</p>
                <p className="text-xs text-muted-foreground mt-1">endereços fora dos envios</p>
              </CardContent>
            </Card>

            {(summary ?? []).filter((s: any) => s.count > 0).map((s: any) => (
              <Card
                key={s.key}
                role="button"
                tabIndex={0}
                onClick={() => changeFilter(() => setReason(s.key))}
                className={`cursor-pointer transition-all hover:shadow-md ${reason === s.key ? "ring-2 ring-rose-400" : ""}`}
              >
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm text-muted-foreground">{s.label}</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-3 w-3 text-muted-foreground shrink-0" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p>{s.description}</p>
                        <p className="mt-1 font-medium">{s.advice}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <p className={`text-2xl font-bold mt-1 ${s.suppresses ? "text-rose-600" : "text-amber-600"}`}>
                    {s.count.toLocaleString("pt-BR")}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {s.suppresses ? "não recebe mais" : "continua recebendo"}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Filtros */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por endereço…"
                value={search}
                onChange={(e) => changeFilter(() => setSearch(e.target.value))}
                className="pl-9"
              />
            </div>
            <Select
              value={reason}
              onValueChange={(v) => changeFilter(() => setReason(v as EmailHygieneReason | "all"))}
            >
              <SelectTrigger className="w-[220px]">
                <SelectValue placeholder="Todos os motivos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os motivos</SelectItem>
                {reasonOptions.map((s: any) => (
                  <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={showActive ? "active" : "reactivated"}
              onValueChange={(v) => changeFilter(() => setShowActive(v === "active"))}
            >
              <SelectTrigger className="w-[190px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Suprimidos</SelectItem>
                <SelectItem value="reactivated">Reativados por mim</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Barra de ação em massa */}
          {selectedIds.length > 0 && (
            <Card className="p-3 border-foreground/20">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm font-medium">
                  {affected.toLocaleString("pt-BR")} selecionado{affected === 1 ? "" : "s"}
                </span>

                {/* O pulo do gato: sair da página visível para o filtro inteiro. */}
                {!selectAllMatching && allOnPageSelected && total > rows.length && (
                  <Button variant="link" size="sm" className="h-auto p-0" onClick={() => setSelectAllMatching(true)}>
                    Selecionar todos os {total.toLocaleString("pt-BR")} do filtro
                  </Button>
                )}
                {selectAllMatching && (
                  <Button variant="link" size="sm" className="h-auto p-0" onClick={clearSelection}>
                    Limpar seleção
                  </Button>
                )}

                <div className="flex-1" />

                {canEdit && showActive && (
                  <Button variant="outline" size="sm" onClick={() => setConfirmAction("reactivate")}>
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Voltar a enviar
                  </Button>
                )}
                {canDelete && (
                  <Button variant="destructive" size="sm" onClick={() => setConfirmAction("delete")}>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Excluir da base
                  </Button>
                )}
              </div>
            </Card>
          )}

          {/* Tabela */}
          <Card>
            <CardContent className="p-0">
              {isLoading ? (
                <div className="p-8 text-center text-sm text-muted-foreground">Carregando…</div>
              ) : rows.length === 0 ? (
                <Empty className="py-12">
                  <EmptyHeader>
                    <EmptyMedia variant="icon"><ShieldOff className="h-6 w-6" /></EmptyMedia>
                    <EmptyTitle>
                      {showActive ? "Nenhum endereço suprimido" : "Nada foi reativado"}
                    </EmptyTitle>
                    <EmptyDescription>
                      {showActive
                        ? "Sua base está limpa para os filtros escolhidos."
                        : "Endereços que você mandar voltar a receber aparecem aqui."}
                    </EmptyDescription>
                  </EmptyHeader>
                </Empty>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                        <tr>
                          <th className="px-3 py-2 w-10">
                            <Checkbox checked={allOnPageSelected} onCheckedChange={toggleAllOnPage} />
                          </th>
                          <th className="px-4 py-2 text-left font-medium">E-mail</th>
                          <th className="px-4 py-2 text-left font-medium">Contato</th>
                          <th className="px-4 py-2 text-left font-medium">Município</th>
                          <th className="px-4 py-2 text-left font-medium">Motivo</th>
                          <th className="px-4 py-2 text-left font-medium">Desde</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r: any) => {
                          const def = EMAIL_HYGIENE_REASON_DEFS[r.reason as EmailHygieneReason];
                          const isSelected = selectedIds.includes(r.contactId);
                          return (
                            <tr key={r.id} className="border-t hover:bg-muted/30">
                              <td className="px-3 py-2">
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => {
                                    setSelectAllMatching(false);
                                    setSelectedIds((prev) => isSelected
                                      ? prev.filter((id) => id !== r.contactId)
                                      : [...prev, r.contactId]);
                                  }}
                                />
                              </td>
                              <td className="px-4 py-2 font-mono text-xs break-all">{r.email}</td>
                              <td className="px-4 py-2">
                                <div className="truncate max-w-[220px]">{r.contactName ?? "—"}</div>
                                {r.companyName && (
                                  <div className="text-xs text-muted-foreground truncate max-w-[220px]">
                                    {r.companyName}
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap">
                                {r.city ? `${r.city}${r.state ? `/${r.state}` : ""}` : "—"}
                              </td>
                              <td className="px-4 py-2">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge variant={def?.suppresses ? "destructive" : "secondary"}>
                                      {r.reasonLabel}
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-md">
                                    <p>{def?.description}</p>
                                    {/* Resposta crua do servidor remoto — é o que
                                        responde "por que exatamente" quando o
                                        usuário desconfia da categoria. */}
                                    {r.detail && (
                                      <p className="mt-1 font-mono text-xs opacity-80">{r.detail}</p>
                                    )}
                                  </TooltipContent>
                                </Tooltip>
                                {r.source === "manual" && (
                                  <span className="ml-2 text-xs text-muted-foreground">manual</span>
                                )}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap text-muted-foreground">
                                {r.suppressedAt
                                  ? new Date(r.suppressedAt).toLocaleDateString("pt-BR")
                                  : "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex items-center justify-between p-3 border-t">
                    <span className="text-xs text-muted-foreground">
                      Página {page + 1} de {Math.max(1, Math.ceil(total / PAGE_SIZE))} — {total.toLocaleString("pt-BR")} endereço(s)
                    </span>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                        Anterior
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={(page + 1) * PAGE_SIZE >= total}
                        onClick={() => setPage((p) => p + 1)}
                      >
                        Próxima
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        <Dialog open={confirmAction !== null} onOpenChange={(o) => !o && setConfirmAction(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {confirmAction === "delete"
                  ? `Excluir ${affected.toLocaleString("pt-BR")} contato(s) da base?`
                  : `Voltar a enviar para ${affected.toLocaleString("pt-BR")} endereço(s)?`}
              </DialogTitle>
              <DialogDescription>
                {confirmAction === "delete" ? (
                  <>
                    Os contatos saem do CRM, das listas e dos públicos de envio. Não dá para desfazer.
                    O histórico das campanhas já enviadas continua intacto.
                    <br /><br />
                    {/* A supressão é chaveada pelo cadastro, não pelo endereço: o
                        texto anterior prometia bloqueio após reimportação, e isso
                        era falso. Melhor avisar do risco real do que garantir o
                        que o sistema não cumpre. */}
                    <strong>Atenção:</strong> se o mesmo endereço voltar numa importação futura, ele
                    entra como contato novo e volta a receber. Se a lista de origem ainda tiver esses
                    endereços, prefira "Voltar a enviar" desmarcado e mantenha-os aqui, suprimidos.
                    <br /><br />
                    Contatos com oportunidade no CRM são mantidos e listados depois.
                  </>
                ) : (
                  <>
                    Esses endereços voltam a entrar nos próximos envios. Use apenas se souber que
                    o problema foi resolvido — reenviar para conta inexistente derruba a entrega de
                    toda a base.
                    <br /><br />
                    Quem se descadastrou ou denunciou spam <strong>não é reativado</strong>: isso
                    depende de consentimento novo, não de um botão.
                    <br /><br />
                    {/* O SendGrid mantém a lista de supressão dele, que não temos
                        como limpar daqui. Sem este aviso, o usuário reativa, o
                        endereço continua sem receber e a tela parece quebrada. */}
                    Endereços que retornaram também ficam na lista de bloqueio do provedor de envio.
                    Reativar aqui não limpa aquela lista — pode ser preciso removê-los no painel do
                    SendGrid para que voltem a receber de fato.
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmAction(null)}>Cancelar</Button>
              <Button
                variant={confirmAction === "delete" ? "destructive" : "default"}
                disabled={deleteMut.isPending || reactivateMut.isPending}
                onClick={() => {
                  if (confirmAction === "delete") deleteMut.mutate(actionTarget as any);
                  else reactivateMut.mutate(actionTarget as any);
                }}
              >
                {deleteMut.isPending || reactivateMut.isPending
                  ? "Processando…"
                  : confirmAction === "delete" ? "Excluir" : "Voltar a enviar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DashboardLayout>
    </PermissionGuard>
  );
}
